// Keyboard shortcuts:
//   Space                  — save & advance (same as the Next button: audit the
//                            current frame, autosave the draft, advance)
//   ArrowLeft / ArrowRight — navigate to the previous / next frame WITHOUT
//                            auditing or saving (pure browsing)
//   ShiftLeft / ShiftRight — toggle free-drag (bypass epipolar constraint)
//   Delete                 — mark the selected keypoint as out_of_frame
//   Ctrl/Cmd+Z             — undo
//   Ctrl/Cmd+Y / Ctrl+Shift+Z — redo

import * as React from 'react';
import { advanceFrame } from '../lib/advance';
import { useStore } from '../store';
import { stepNavigable } from '../store/video';

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  const html = el as HTMLElement;
  return html.isContentEditable === true;
}

export function useKeyboardShortcuts(): void {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      // ── Undo / Redo (Ctrl/Cmd+Z, Ctrl/Cmd+Y, Ctrl/Cmd+Shift+Z) ────────
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'z' || e.key === 'Z' || e.key === 'y' || e.key === 'Y')
      ) {
        if (isTypingTarget(document.activeElement)) return;
        e.preventDefault();
        const isRedo = e.key === 'y' || e.key === 'Y' || e.shiftKey; // Ctrl+Y or Ctrl+Shift+Z
        const s = useStore.getState();
        if (isRedo) s.redo();
        else s.undo();
        return;
      }

      // ── Toggle free-drag (either Shift key) ─────────────────────────
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        if (isTypingTarget(document.activeElement)) return;
        e.preventDefault();
        useStore.getState().toggleFreeDrag();
        return;
      }

      // ── Mark selected keypoint as out_of_frame (Delete) ───────────────
      if (e.key === 'Delete') {
        if (isTypingTarget(document.activeElement)) return;
        e.preventDefault();
        const s = useStore.getState();
        const activeView = s.activeView;
        if (!activeView) return;
        const selectedName =
          activeView === 'front_left'
            ? s.selectedFrontLeft
            : activeView === 'front_right'
              ? s.selectedFrontRight
              : activeView === 'left'
                ? s.selectedLeft
                : s.selectedRight;
        if (!selectedName) return;
        s.setVisibility(activeView, s.currentFrame, selectedName, 'out_of_frame');
        return;
      }

      // ── Save & advance (Space) — same as the Next button ──────────────
      if (e.key === ' ') {
        if (isTypingTarget(document.activeElement)) return;
        e.preventDefault();
        if (e.repeat) return; // ignore key-repeat; advanceFrame guards re-entry
        void advanceFrame();
        return;
      }

      // ── Navigate without saving (ArrowLeft / ArrowRight) ──────────────
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (isTypingTarget(document.activeElement)) return;
        e.preventDefault();
        const s = useStore.getState();
        if (s.totalFrames <= 0) return;
        const delta = e.key === 'ArrowLeft' ? -1 : 1;
        if (s.navigableFrames && s.navigableFrames.length > 0) {
          s.setCurrentFrame(stepNavigable(s.navigableFrames, s.currentFrame, delta));
        } else {
          // No picks: ±1 raw frame. setCurrentFrame doesn't clamp here, so do it.
          const next = Math.max(0, Math.min(s.totalFrames - 1, s.currentFrame + delta));
          s.setCurrentFrame(next);
        }
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, []);
}
